import { useCallback, useState } from 'react'
import { uniq } from 'lodash'

import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { useDispatch, useSelector } from '@tabeeb/store'
import { callApiAsync } from '@tabeeb/modules/shared/utils/requests'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'

import { deleteUniqueAiObject } from '../actions'

const useUniqueAIObjectsSelection = () => {
  const dispatch = useDispatch()

  const [deletionInProgress, setDeletionInProgress] = useState(false)
  const contentId = useSelector(getContentId)

  const [selectionModeActive, setSelectionModeActive] = useState(false)
  const [selectedObjectIds, setSelectedObjectIds] = useState<number[]>([])

  const onToggleSelectionMode = useCallback((enabled) => {
    setSelectionModeActive(enabled)
    setSelectedObjectIds([])
  }, [])

  const onAddObjectToSelection = useCallback(
    (uniqueAIObjects: { Id: number }[]) => {
      setSelectedObjectIds((prev) => uniq([...prev, ...uniqueAIObjects.map((o) => o.Id)]))
    },
    [setSelectedObjectIds]
  )

  const onRemoveObjectFromSelection = useCallback(
    (uniqueAIObjects: { Id: number }[]) => {
      const idsToRemove = new Set(uniqueAIObjects.map((o) => o.Id))

      setSelectedObjectIds((prev) => prev.filter((id) => !idsToRemove.has(id)))
    },
    [setSelectedObjectIds]
  )

  const onClearSelection = useCallback(() => {
    setSelectedObjectIds([])
  }, [setSelectedObjectIds])

  const onDeleteSelectedObjects = useCallback(() => {
    const deleteUniqueAIObjects = async () => {
      try {
        setDeletionInProgress(true)

        await Promise.all(
          selectedObjectIds.map((uniqueAIObjectId) =>
            callApiAsync(
              deleteUniqueAiObject.request({
                contentId,
                uniqueAIObjectId,
              })
            )
          )
        )

        setSelectionModeActive(false)
        setSelectedObjectIds([])
      } catch {
        dispatch(
          onAddErrorNotification({
            message: 'Failed to delete selected objects',
          })
        )
      } finally {
        setDeletionInProgress(false)
      }
    }

    deleteUniqueAIObjects()
  }, [contentId, dispatch, selectedObjectIds])

  return {
    deletionInProgress,
    active: selectionModeActive,
    selected: selectedObjectIds,
    onToggleSelectionMode,
    onAddObjectToSelection,
    onRemoveObjectFromSelection,
    onDeleteSelectedObjects,
    onClearSelection,
  }
}

export default useUniqueAIObjectsSelection
